import React, { useState } from 'react';
import MenuList from '@mui/material/MenuList';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import { useMediaQuery } from '@mui/material';
import get from 'lodash/get';
import classNames from 'classnames';
import { getConfigObject } from '../../../../utils/config';
import MLIcon from '../../../MLIcon';
import useSidePanel from '../../../SidePanel/hooks/useSidePanel';
import MLButton from '../../../MLButton';

import s from './ReefMenu.module.css';

function ReefMenu({
  user,
  permissions,
  hasInstructorLinkedCourse,
  disableEnforceFocus,
  toggleLinkReefDialog,
  toggleUnlinkReefDialog,
  handleSyncReefGrades,
  isSectionManager,
}) {
  const { ICLICKER_INTEGRATION_DISABLED } = getConfigObject();
  const {
    state: { mediaQueries },
  } = useSidePanel();

  const isMedium = useMediaQuery(`(max-width: ${mediaQueries.medium}px)`);

  const initialState = { anchorEl: null, open: false };
  const [menu, setMenu] = useState(initialState);

  const handleOpen = (event) => {
    setMenu({ anchorEl: event.currentTarget, open: true });
  };

  const handleClose = () => {
    setMenu(initialState);
  };

  const handleLinkReefDialog = () => {
    handleClose();
    toggleLinkReefDialog();
  };

  const itemStyle = {
    minHeight: 'fit-content',
    lineHeight: 'normal',
    fontSize: '1rem',
    padding: '9px 9px',
    borderTop: '1px solid #DDDDDD',
  };

  const listStyles = {
    paddingTop: 0,
    paddingBottom: 0,
  };

  const isReefLinked = get(user, 'isReefLinked', false);
  const canLinkReefCourse = get(
    permissions,
    'course.reef.linking.course',
    false,
  );
  const canSyncGrades = get(permissions, 'course.reef.gradeSync', false);
  const isReefStatusFetching = get(user, 'isReefStatusFetching');

  const renderReefSyncInfo = () => {
    const reefEmail = get(user, 'reefProfile.email', null);
    const reefFirstName = get(user, 'reefProfile.firstName', null);
    const reefLastName = get(user, 'reefProfile.lastName', null);
    const reefCourseName = get(user, 'reefCourseName', 'N/A');
    const reefCourseId = get(user, 'reefCourseId');

    return (
      <MenuItem
        data-test-id="iclicker_info"
        className={s.accountInfoMenuItem}
        style={{
          ...itemStyle,
          borderTop: 'none',
          backgroundColor: '#F3F3F3',
          alignItems: 'stretch',
          flexDirection: 'column',
        }}
      >
        <div className={s.reefSyncInfoInnerMenuSection}>
          <div>Account:</div>
          {reefFirstName && reefLastName && (
            <div>
              {reefFirstName} {reefLastName}
            </div>
          )}
          {reefEmail && <div>{reefEmail}</div>}
        </div>
        <div
          className={s.reefSyncInfoInnerMenuSection}
          data-test-id="iclicker_info_link_course"
        >
          <div>Connected To:</div>
          {reefCourseId ? (
            <div>{reefCourseName}</div>
          ) : (
            <div>
              None,&nbsp;
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a role="button" onClick={handleLinkReefDialog}>
                Link a course now.
              </a>
            </div>
          )}
        </div>
      </MenuItem>
    );
  };

  const renderConnectCourseReef = () => {
    const text = 'Connect to Course';
    const borderTop = '1px solid #DDDDDD';

    return (
      <MenuItem
        onClick={handleLinkReefDialog}
        style={{
          ...itemStyle,
          borderTop,
        }}
      >
        {text}
      </MenuItem>
    );
  };

  const renderSyncReefGrades = () => {
    if (!user.reefCourseId) {
      return null;
    }
    return (
      <MenuItem
        onClick={handleSyncReefGrades}
        style={itemStyle}
        data-test-id="iclicker-refresh-grades"
      >
        Refresh iClicker Grades
      </MenuItem>
    );
  };

  const renderDisconnectReef = () => {
    return (
      <MenuItem
        style={itemStyle}
        onClick={() => {
          handleClose();
          toggleUnlinkReefDialog();
        }}
      >
        <span data-test-id="iclicker_menu_disconnect">Disconnect iClicker</span>
      </MenuItem>
    );
  };

  const renderMenuButton = ({ onClick, label, hideDownIcon = false }) => {
    return (
      <MLButton
        btnType="secondary"
        className={classNames(s.menuButton, {
          [s.menuButtonMedium]: isMedium,
        })}
        ariaExpanded={menu.open}
        ariaLabel={label}
        testId="reef-options-button"
        onClick={onClick}
      >
        <MLIcon
          viewBox="0 0 18 18"
          type="iclicker_cloud"
          className={classNames(s.buttonLeftIcon, {
            [s.buttonLeftIconMedium]: isMedium,
          })}
        />
        <Typography
          className={classNames(s.buttonLabel, {
            [s.buttonLabelMedium]: isMedium,
          })}
        >
          {label}
        </Typography>
        <MLIcon
          type="caret_down"
          className={classNames(s.buttonRightIcon, {
            [s.buttonRightIconMedium]: isMedium || hideDownIcon,
          })}
        />
      </MLButton>
    );
  };

  return (
    !isSectionManager &&
    !ICLICKER_INTEGRATION_DISABLED &&
    canSyncGrades &&
    !isReefStatusFetching && (
      <>
        {!isReefLinked &&
          canLinkReefCourse &&
          renderMenuButton({
            onClick: toggleLinkReefDialog,
            label: 'Sync iClicker',
            hideDownIcon: true,
          })}
        {isReefLinked &&
          renderMenuButton({
            onClick: handleOpen,
            label: 'iClicker',
          })}
        <Popover
          open={menu.open}
          anchorEl={menu.anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          onClose={handleClose}
          disableEnforceFocus={disableEnforceFocus}
        >
          <MenuList liststyle={listStyles} className={s.menuList}>
            {renderReefSyncInfo()}
            {!hasInstructorLinkedCourse && renderConnectCourseReef()}
            {canSyncGrades && renderSyncReefGrades()}
            {isReefLinked && canLinkReefCourse && renderDisconnectReef()}
            <a
              href={getConfigObject().REEF_GO_TO_ICLICKER_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className={s.linkNoUnderline}
            >
              <MenuItem style={itemStyle}>
                <span data-test-id="manage-iclicker-menu">Manage iClicker</span>
              </MenuItem>
            </a>
          </MenuList>
        </Popover>
      </>
    )
  );
}

export default ReefMenu;
